/**
 * Hilo de conversacion con Androcles.
 *
 * Muestra los turnos (tabla `turnos`) en burbujas grandes y permite escribir
 * por teclado como alternativa a la voz. La voz llega por WebSocket
 * (`conectarConversacion`) y se pinta como turno parcial hasta que cierra.
 *
 * Regla de seguridad de interfaz: el texto que viene del banco o de conectores
 * es DATO NO CONFIABLE. Aqui se renderiza siempre como texto plano —nunca como
 * HTML— para que un concepto bancario no pueda inyectar nada en la pantalla.
 */
import { useEffect, useRef, useState } from 'react';
import { Boton } from '@/components/ui/button';
import { GrabarMuestraVoz } from '@/components/GrabarMuestraVoz';
import { cn, formatearHora } from '@/lib/utils';
import { api, type OpcionVoz, type Turno, type VozActiva } from '@/lib/api';
import * as reproductor from '@/lib/reproductor';
import type { MensajeAmable } from '@/lib/audio';

interface PropsConversacion {
  turnos: Turno[];
  /** true mientras el agente razona o invoca herramientas. */
  pensando?: boolean;
  /** Herramienta en curso, para dar sensacion de trabajo real. */
  herramientaEnCurso?: string | null;
  /** Envio por teclado; si no se pasa, el kiosco queda en solo voz. */
  alEnviarTexto?: (texto: string) => void;

  // --- Voz conversacional (canal PRINCIPAL para el jefe) ---
  /** true si este equipo puede grabar: pinta o no el boton de microfono. */
  soportaVoz?: boolean;
  /** true si el microfono esta capturando la voz del jefe. */
  escuchando?: boolean;
  /** Volumen del microfono, 0..1, para el indicador de "le escucho". */
  nivel?: number;
  /** true mientras suena la voz de Androcles (TTS de la respuesta). */
  hablando?: boolean;
  /** true si el jefe ha silenciado la voz (las respuestas siguen en texto). */
  silenciada?: boolean;
  /** Lo que el STT va oyendo antes de cerrar el turno; se pinta en gris. */
  textoParcial?: string;
  /** Fallo del microfono, ya traducido a lenguaje de persona. */
  aviso?: MensajeAmable | null;
  /** Enciende o apaga el microfono. */
  alAlternarEscucha?: () => void;
  /** Silencia o devuelve la voz de Androcles. */
  alAlternarSilencio?: () => void;

  // --- Fin de la charla ---
  /** true si la conversacion ya se dio por terminada. */
  terminada?: boolean;
  /** Termina la charla (despedida + cierre de la sesion de voz). */
  alFinalizar?: () => void;
  /** Vuelve a levantar la conversacion despues de haberla terminado. */
  alReanudar?: () => void;

  className?: string;
}

export function Conversacion({
  turnos,
  pensando = false,
  herramientaEnCurso = null,
  alEnviarTexto,
  soportaVoz = false,
  escuchando = false,
  nivel = 0,
  hablando = false,
  silenciada = false,
  textoParcial = '',
  aviso = null,
  alAlternarEscucha,
  alAlternarSilencio,
  terminada = false,
  alFinalizar,
  alReanudar,
  className,
}: PropsConversacion) {
  const [borrador, setBorrador] = useState('');
  const finRef = useRef<HTMLDivElement>(null);

  // Autoscroll al ultimo turno: el jefe nunca deberia tener que arrastrar.
  useEffect(() => {
    finRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [turnos, pensando, textoParcial]);

  const enviar = () => {
    const texto = borrador.trim();
    if (!texto || !alEnviarTexto) return;
    alEnviarTexto(texto);
    setBorrador('');
  };

  return (
    <section
      className={cn('flex min-h-0 flex-col gap-5', className)}
      aria-label="Conversación con Androcles"
    >
      {alAlternarEscucha && (
        <BarraVoz
          soportaVoz={soportaVoz}
          escuchando={escuchando}
          nivel={nivel}
          hablando={hablando}
          silenciada={silenciada}
          aviso={aviso}
          alAlternarEscucha={alAlternarEscucha}
          alAlternarSilencio={alAlternarSilencio}
          terminada={terminada}
          alFinalizar={alFinalizar}
          alReanudar={alReanudar}
        />
      )}

      <div
        // Alto propio: al menos 8rem para que no quede una rendija, y como mucho
        // 45vh para que el hilo no empuje al formulario fuera de la pantalla.
        className="flex min-h-[8rem] flex-1 flex-col gap-5 overflow-y-auto pr-3 lg:max-h-[45vh]"
        // Los turnos nuevos se anuncian al lector de pantalla sin robar el foco.
        aria-live="polite"
        aria-atomic="false"
      >
        {turnos.length === 0 && !pensando && !textoParcial && <ConversacionVacia />}

        {turnos.map((turno) => (
          <BurbujaTurno key={`${turno.id}-${turno.orden}`} turno={turno} />
        ))}

        {textoParcial && <BurbujaEscuchando texto={textoParcial} />}
        {pensando && <BurbujaPensando herramienta={herramientaEnCurso} />}
        <div ref={finRef} />
      </div>

      {alEnviarTexto && (
        <form
          // Mismo motivo que en BarraVoz: en la columna estrecha de escritorio
          // el textarea (ancho por defecto ~20 caracteres) mas el boton no
          // caben en fila y se salian de la tarjeta. Fila solo hasta `lg`.
          className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-end sm:gap-4 lg:flex-col lg:items-stretch"
          onSubmit={(e) => {
            e.preventDefault();
            enviar();
          }}
        >
          <label className="sr-only" htmlFor="entrada-texto">
            Escriba su pregunta a Androcles
          </label>
          <textarea
            id="entrada-texto"
            value={borrador}
            onChange={(e) => setBorrador(e.target.value)}
            onKeyDown={(e) => {
              // Enter envia; Mayus+Enter hace parrafo.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                enviar();
              }
            }}
            rows={2}
            placeholder="Escriba aquí si prefiere no hablar…"
            // `w-full min-w-0`: sin esto el textarea impone su ancho intrinseco
            // (el atributo `cols` por defecto) y desborda la columna.
            className="min-h-tactil w-full min-w-0 flex-1 resize-none rounded-lg border-2 border-input
                       bg-background px-6 py-4 text-base leading-relaxed placeholder:text-muted-foreground"
          />
          <Boton type="submit" disabled={!borrador.trim()} aria-label="Enviar mensaje">
            Enviar
          </Boton>
        </form>
      )}
    </section>
  );
}

/** Numero de barras del vumetro de la barra de voz. */
const BARRAS_VOZ = 10;

/**
 * Barra de voz: el canal PRINCIPAL de la conversacion.
 *
 * El jefe tiene 88 anos y habla antes que teclea, asi que el microfono manda:
 * boton grande, de alto contraste, con un indicador enorme de "le escucho"
 * (vumetro + rotulo literal) y otro de "estoy hablando" cuando suena la voz.
 * Aparte, un boton para silenciar la voz sin renunciar al texto.
 *
 * Si el equipo no puede grabar, el boton de microfono no aparece (se conversa
 * por teclado abajo) pero el aviso explica que hacer, sin jerga.
 */
function BarraVoz({
  soportaVoz,
  escuchando,
  nivel,
  hablando,
  silenciada,
  aviso,
  alAlternarEscucha,
  alAlternarSilencio,
  terminada,
  alFinalizar,
  alReanudar,
}: {
  soportaVoz: boolean;
  escuchando: boolean;
  nivel: number;
  hablando: boolean;
  silenciada: boolean;
  aviso: MensajeAmable | null;
  alAlternarEscucha: () => void;
  alAlternarSilencio?: () => void;
  terminada: boolean;
  alFinalizar?: () => void;
  alReanudar?: () => void;
}) {
  const encendidas = escuchando ? Math.round(Math.min(1, Math.max(0, nivel)) * BARRAS_VOZ) : 0;
  const oyendo = escuchando && encendidas > 0;

  const rotulo = terminada
    ? 'Conversación terminada. Pulse para volver a hablar.'
    : hablando
      ? 'Estoy hablando…'
      : aviso
        ? 'El micrófono no está funcionando'
        : !escuchando
          ? 'Pulse y hábleme'
          : // Desde el 10/09/2026 el turno se cierra SOLO al callarse: el
            // boton es el atajo para no esperar esa pausa, no la unica forma
            // de que Androcles conteste. El rotulo tiene que decir eso, que es
            // lo que quita la sensacion de tener que estar pendiente.
            oyendo
            ? 'Le escucho. Cuando calle, le contesto.'
            : 'Le escucho. Hable cuando quiera.';

  return (
    <div className="shrink-0 rounded-lg border border-border/70 bg-card/60 p-5">
      {/*
        OJO con los breakpoints aqui: `sm:` mira el ANCHO DE LA VENTANA, no el
        de esta columna. En escritorio la columna izquierda mide 23-28rem, asi
        que `sm:flex-row` ponia boton + vumetro + «Silenciar voz» en fila dentro
        de ~330px utiles y los tres se pisaban (reporte «la version PC esta
        descuadrada», 29/07/2026). Regla: fila solo en la franja en la que esta
        columna ocupa el ancho completo (movil/tablet, < lg); a partir de `lg`
        vuelve a apilarse.
      */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center lg:flex-col lg:items-stretch">
        {soportaVoz && (
          <Boton
            variante={escuchando ? 'destructivo' : 'principal'}
            tamano="grande"
            className="shrink-0 sm:w-64 lg:w-full"
            onClick={alAlternarEscucha}
            aria-pressed={escuchando}
          >
            {/* Tamano en un span propio: a ancho completo en movil, la etiqueta
                a 26px se salia del boton (se cortaban la H y la s). A 22px cabe
                y sigue calida. En span para no chocar con el text-lg del boton. */}
            <span className="whitespace-nowrap text-base sm:text-lg">
              {/* Mientras escucha, el boton dice lo que HACE EL JEFE al
                  pulsarlo, no lo que quiere del programa. "Ya está,
                  contesteme" sonaba a "date prisa" y no contaba lo importante:
                  que corta lo que se lleva dicho y APAGA el microfono
                  (Ricardo, 10/09/2026: "creo que el texto no lo describe
                  bien"). Ahora, desde que el corte por silencio funciona, esto
                  es solo el atajo para no esperar la pausa. */}
              {terminada
                ? 'Volver a hablar'
                : escuchando
                  ? 'He terminado de hablar'
                  : 'Hablar con Androcles'}
            </span>
          </Boton>
        )}

        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex h-12 items-end gap-1.5" aria-hidden="true">
            {hablando
              ? Array.from({ length: BARRAS_VOZ }, (_, i) => (
                  <span
                    key={i}
                    className="flex-1 animate-pulse rounded-sm bg-accent"
                    style={{ height: `${40 + Math.abs(5 - i) * 8}%`, animationDelay: `${i * 0.08}s` }}
                  />
                ))
              : Array.from({ length: BARRAS_VOZ }, (_, i) => {
                  const activa = i < encendidas;
                  return (
                    <span
                      key={i}
                      className={cn(
                        'flex-1 rounded-sm border-2 transition-[height,background-color] duration-100',
                        activa
                          ? 'border-[hsl(var(--cobro))] bg-[hsl(var(--cobro))]'
                          : 'border-border bg-transparent',
                      )}
                      style={{ height: `${35 + (i / (BARRAS_VOZ - 1)) * 65}%` }}
                    />
                  );
                })}
          </div>
          <p
            className={cn(
              'text-lg font-bold',
              aviso
                ? 'text-destructive'
                : hablando
                  ? 'text-accent'
                  : oyendo
                    ? 'text-[hsl(var(--cobro))]'
                    : 'text-muted-foreground',
            )}
            aria-live="polite"
          >
            {(escuchando || hablando) && !aviso && (
              <span
                className={cn(
                  'mr-3 inline-block h-4 w-4 rounded-full align-middle animate-pulse',
                  hablando ? 'bg-accent' : 'bg-[hsl(var(--cobro))]',
                )}
                aria-hidden="true"
              />
            )}
            {rotulo}
          </p>
        </div>

        {alAlternarSilencio && (
          <Boton
            variante="contorno"
            className="shrink-0 lg:w-full"
            onClick={alAlternarSilencio}
            aria-pressed={silenciada}
          >
            {silenciada ? 'Activar voz' : 'Silenciar voz'}
          </Boton>
        )}

        <SelectorVoz />

        {/*
          Cerrar la charla explicitamente. Hasta ahora no habia manera de
          decir "ya hemos terminado" salvo irse de la pantalla, y la sesion de
          voz se quedaba abierta con el microfono a mano (peticion del jefe,
          04/08/2026). Tambien vale decirlo en voz alta: el backend reconoce
          «finalizar conversacion», «adios» y companeras.
        */}
        {(alFinalizar || alReanudar) && (
          <Boton
            variante="contorno"
            className="shrink-0 lg:w-full"
            onClick={terminada ? alReanudar : alFinalizar}
          >
            {terminada ? 'Empezar de nuevo' : 'Finalizar conversación'}
          </Boton>
        )}
      </div>

      {aviso && (
        <div
          className="mt-4 animate-entrada-suave rounded-lg border-2 border-destructive bg-destructive/10 p-5"
          role="alert"
        >
          <p className="text-lg font-bold text-destructive">{aviso.titulo}</p>
          <p className="mt-2 text-base leading-relaxed">{aviso.consejo}</p>
        </div>
      )}
    </div>
  );
}

/**
 * Selector de voz, para ir probando motores sin reiniciar el servicio.
 *
 * Peticion de Ricardo (04/08/2026): la voz de Piper suena robotica y hay que
 * poder comparar candidatas con el jefe delante. Al elegir una, se dice una
 * frase de muestra con ella: es la unica forma honesta de comparar.
 *
 * Las voces cuyo modelo no esta descargado salen deshabilitadas en vez de
 * ocultas: asi se ve que existen y que solo falta bajarlas.
 */
function SelectorVoz() {
  const [opciones, setOpciones] = useState<OpcionVoz[]>([]);
  const [activa, setActiva] = useState<VozActiva | null>(null);
  const [probando, setProbando] = useState(false);

  useEffect(() => {
    let vivo = true;
    api
      .voces()
      .then((r) => {
        if (!vivo) return;
        setOpciones(r.opciones);
        setActiva(r.activa);
      })
      .catch(() => undefined); // sin catalogo, el selector no se pinta
    return () => {
      vivo = false;
    };
  }, []);

  if (!activa || opciones.length === 0) return null;

  const clave = `${activa.motor}|${activa.voz}`;

  const cambiar = async (valor: string) => {
    const [motor, voz] = valor.split('|');
    setProbando(true);
    try {
      // El desbloqueo va en el gesto del usuario: en movil, si no, el audio
      // de muestra no suena (mismo motivo que en el boton de hablar).
      reproductor.desbloquear();
      setActiva(await api.elegirVoz(motor, voz));
      const wav = await api.sintetizarVoz(
        'Buenas tardes, jefe. Asi es como sueno con esta voz.',
      );
      reproductor.encolarAudio(wav);
    } catch {
      // Si la voz falla, el backend sigue con la anterior; no se rompe nada.
    } finally {
      setProbando(false);
    }
  };

  return (
    <>
    <div className="mt-4 flex items-center gap-3">
      <label className="shrink-0 text-sm text-muted-foreground" htmlFor="selector-voz">
        Voz
      </label>
      <select
        id="selector-voz"
        value={clave}
        disabled={probando}
        onChange={(e) => void cambiar(e.target.value)}
        className="min-w-0 flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
      >
        {opciones.map((o) => (
          <option key={`${o.motor}|${o.voz}`} value={`${o.motor}|${o.voz}`} disabled={!o.disponible}>
            {o.etiqueta}
            {o.disponible ? '' : ' (sin descargar)'}
          </option>
        ))}
      </select>
      {probando && <span className="shrink-0 text-sm text-muted-foreground">probando…</span>}
    </div>
    {/* Grabar la muestra para clonar la voz: se hace desde el equipo que
        tenga microfono, que no es el servidor. */}
    <GrabarMuestraVoz className="mt-3" />
    </>
  );
}

/** Burbuja de lo que el jefe esta diciendo, antes de cerrar el turno. */
function BurbujaEscuchando({ texto }: { texto: string }) {
  return (
    <article className="max-w-[85%] self-end rounded-lg border-2 border-dashed border-primary/50 bg-primary/10 px-7 py-5">
      <header className="mb-2 flex items-baseline gap-4">
        <span className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
          Usted
        </span>
      </header>
      <p className="whitespace-pre-wrap text-base leading-relaxed opacity-80">
        {texto}
        <span className="ml-1 animate-pulse">▌</span>
      </p>
    </article>
  );
}

/** Burbuja de un turno. Colores por rol, siempre con etiqueta textual. */
function BurbujaTurno({ turno }: { turno: Turno }) {
  const esJefe = turno.rol === 'jefe';
  const esSistema = turno.rol === 'sistema';

  return (
    <article
      className={cn(
        'max-w-[85%] animate-entrada-suave rounded-lg border-2 px-7 py-5',
        esJefe && 'self-end border-primary/50 bg-primary/15',
        !esJefe && !esSistema && 'self-start border-border bg-card',
        esSistema && 'self-center border-dashed border-border bg-transparent text-sm italic',
      )}
    >
      <header className="mb-2 flex items-baseline gap-4">
        <span className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
          {esJefe ? 'Usted' : esSistema ? 'Sistema' : 'Androcles'}
        </span>
        <time className="text-sm text-muted-foreground">{formatearHora(turno.ts)}</time>
      </header>

      {/* Texto plano deliberado: nunca dangerouslySetInnerHTML. */}
      <p className={cn('whitespace-pre-wrap text-base leading-relaxed', turno.parcial && 'opacity-70')}>
        {turno.texto}
        {turno.parcial && <span className="ml-1 animate-pulse">▌</span>}
      </p>
    </article>
  );
}

/** Indicador de trabajo en curso; nombra la herramienta si se conoce. */
function BurbujaPensando({ herramienta }: { herramienta: string | null }) {
  return (
    <div className="flex items-center gap-4 self-start rounded-lg border-2 border-dashed border-border px-7 py-5">
      <span className="flex gap-2" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="h-3 w-3 animate-pulse rounded-full bg-accent"
            style={{ animationDelay: `${i * 0.2}s` }}
          />
        ))}
      </span>
      <span className="text-base text-muted-foreground">
        {herramienta ? `Consultando (${herramienta})…` : 'Un momento, lo estoy mirando…'}
      </span>
    </div>
  );
}

/** Estado inicial: sugerencias en lenguaje natural, no botones crípticos. */
function ConversacionVacia() {
  return (
    // `max-w-full`: en la columna estrecha, un `max-w-2xl` (672px) forzaba el
    // ancho del contenedor y recortaba el texto por los lados.
    <div className="m-auto w-full max-w-full text-center">
      <p className="text-2xl font-semibold">Buenos días. ¿En qué puedo ayudarle?</p>
      <p className="mt-5 text-base text-muted-foreground">
        Puede preguntarme, por ejemplo: «¿cómo estamos de caja?», «¿qué pagos hay esta semana?»
        o «léeme el parte de hoy».
      </p>
    </div>
  );
}
